import { useState, useCallback, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { useIntl } from 'react-intl';

import { Button, IconButton, LogoLoader, Label, ProductSchemaOrg } from 'bf-ui';
import Div100vh from 'react-div-100vh';

import Image from 'next/image';

import { getGame_req, initGameWithUrl_req } from '../../api/game.api';

import styles from '../../assets/styles/pages/game.page.scss';

import noImage from '../../assets/images/additional/no-game-image.jpg';

import StarInactiveGoldIcon from '../../assets/images/icons/star-gold.svg';
import StarActiveGoldIcon from '../../assets/images/icons/star.svg';
import CupIcon from '../../assets/images/icons/cup.png';
import SpinIcon from '../../assets/images/icons/spin.svg';
import PositivePolygonIcon from '../../assets/images/icons/positive-polygon.svg';
import NegativePolygonIcon from '../../assets/images/icons/negative-polygon.svg';

import {
  addFavoriteGame_req,
  deleteFavoriteGame_req,
} from '../../api/user.api';

import globalEmitter, { emits } from '../../emitter/global.emitter';
import ModalsEnum from '../../enums/modals.enum';
import signalRSingleton from '../../singletons/signalR.singleton';
import TournamentEnum from '../../enums/tournament.enum';
import { extractDate, nth } from '../../helpers/date.helper';
import websiteConfig from '../../configs/websiteConfig';
import usePrevious from '../../hooks/previous.hook';
import LinkWrapper from '../../components/wrappers/link/link.wrapper';
import { isClient } from '../../helpers/detector.helper';
import { cacheControl } from '../../helpers/cache.helper';

function getRandomInt(min, max) {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

export default function GamePage({ data }) {
  const iframeRef = useRef();
  const router = useRouter();
  const intl = useIntl();

  const { query } = router;

  const iframeLoadedTimeoutRef = useRef(null);
  const tournamentHubConnected = useRef(false);
  const [gameData, setGameData] = useState(data);
  const [tournamentLabelData, setTournamentLabelData] = useState(null);
  const [imgError, setImgError] = useState(false);
  const [gameUrl, setGameUrl] = useState('');
  const [iframeLoading, setIframeLoading] = useState(false);
  const [iframeOneTimeLoaded, setIframeOneTimeLoaded] = useState(false);
  const [isFavorite, setIsFavorite] = useState(!!gameData?.IsFavorite);

  const prevRank = usePrevious(tournamentLabelData?.Rank);

  const [initError, setInitError] = useState(null);

  const user = useSelector((state) => state.user);

  const playGame = useCallback(async () => {
    try {
      if (!user.id) {
        return router.push(`/login?from=/games/${gameData.Key}`, '/login');
      }

      const initData = await initGameWithUrl_req(
        gameData.InitUrl,
        gameData.Key
      );
      if (initData.url) {
        setGameUrl(initData.url);
      }
    } catch (e) {
      setInitError(true);
      setGameUrl('');
    }

    return null;
  }, [gameData?.InitUrl, gameData?.Key, user]);

  const playDemoGame = useCallback(async () => {
    if (gameData && gameData.Key && gameData.InitDemoUrl) {
      try {
        const initData = await initGameWithUrl_req(
          gameData.InitDemoUrl,
          gameData.Key
        );

        if (initData.url) {
          setGameUrl(initData.url);
        }
      } catch (e) {
        setInitError(true);
        setGameUrl('');
      }
    }
  }, [gameData?.InitDemoUrl, gameData?.Key]);

  const addRemoveFromFavorites = useCallback(async () => {
    try {
      if (!user?.id) {
        return router.push(`/login?from=/games/${gameData.Key}`, '/login');
      }

      if (!isFavorite) {
        await addFavoriteGame_req(gameData.ID);
        setIsFavorite(true);
      } else {
        await deleteFavoriteGame_req(gameData.ID);
        setIsFavorite(false);
      }
    } catch (e) {
      console.error('GamePage -> addRemoveFromFavorites Error:', e);
    }
    return true;
  }, [isFavorite, user]);

  const watchTournamentHubEvents = useCallback(() => {
    if (signalRSingleton.tournamentHubConnection) {
      signalRSingleton.tournamentHubConnection.on('GetPosition', (message) => {
        try {
          message = JSON.parse(message);

          setTournamentLabelData(message);
        } catch (e) {
          console.error(
            'GamePage -> watchTournamentHubEvents -> GetPosition Error:',
            e
          );
        }
      });
    }
  }, [tournamentLabelData, signalRSingleton]);

  const connectToTournamentHub = useCallback(async () => {
    if (!gameData?.TournamentID || tournamentHubConnected.current) {
      return;
    }

    tournamentHubConnected.current = true;
    await signalRSingleton.connectToTournamentHub(gameData.TournamentID);

    if (signalRSingleton.tournamentHubConnected) {
      watchTournamentHubEvents();
    }
  }, [gameData, signalRSingleton, tournamentHubConnected.current]);

  const onTournamentLabel = useCallback(() => {
    if (!gameData?.TournamentID) {
      return;
    }

    globalEmitter.emit(emits.MODAL, {
      id: ModalsEnum.TOURNAMENT,
      data: {
        id: gameData.TournamentID,
      },
    });
  }, [gameData]);

  const loadGame = useCallback(async () => {
    if (gameData || !query.gameKey) {
      return;
    }

    try {
      setGameData(await getGame_req({}, query.gameKey));
    } catch (e) {
      console.error('GamePage -> loadGame Error:', e);
    }
  }, [gameData, query]);

  useEffect(() => {
    if (query?.['game-type'] === 'classic') {
      playGame();
    } else if (query?.['game-type'] === 'demo') {
      playDemoGame();
    }
  }, [gameData, query]);

  useEffect(() => {
    if (gameUrl) {
      if (iframeRef.current) {
        if (!iframeOneTimeLoaded) {
          setIframeLoading(true);
        }

        iframeLoadedTimeoutRef.current = setTimeout(() => {
          setIframeOneTimeLoaded(true);
          setIframeLoading(false);
        }, 2000);

        iframeRef.current.addEventListener('load', () => {
          setIframeOneTimeLoaded(true);
          setIframeLoading(false);
        });
      }
    }

    return () => {
      clearTimeout(iframeLoadedTimeoutRef.current);
    };
  }, [gameUrl]);

  useEffect(() => {
    connectToTournamentHub();
    loadGame();
  }, [gameData]);

  useEffect(
    () => () => {
      signalRSingleton.disconnectFromTournamentHub();
    },
    []
  );

  const getTournamentLabelText = useCallback(() => {
    if (!tournamentLabelData?.Type) {
      return null;
    }

    switch (tournamentLabelData.Type) {
      case TournamentEnum.DATE: {
        const extractedDate = extractDate(tournamentLabelData.StartDate);

        return (
          <div className="live_start_date">
            <div className="label_img_wrapper">
              <Image src={CupIcon} alt="Cup" width={22} height={22} />
            </div>
            {tournamentLabelData.StartDate && (
              <span>
                {new Date(tournamentLabelData.StartDate).getTime() -
                  Date.now() >=
                0 ? (
                  <>
                    Tournament starts in{` `}
                    {extractedDate.day}.{extractedDate.month}.
                    {extractedDate.year}
                  </>
                ) : (
                  'Spin to join the tournament'
                )}
              </span>
            )}
          </div>
        );
      }
      case TournamentEnum.QUALIFICATION: {
        return (
          <>
            <div className="live_position">
              <div className="label_img_wrapper">
                <Image src={SpinIcon} alt="Cup" width={22} height={22} />
              </div>
              <span>
                {tournamentLabelData.SpinCount}/
                {tournamentLabelData.MinSpinCount} Spins to qualify for
                Tournament
              </span>
            </div>
          </>
        );
      }
      case TournamentEnum.POSITION: {
        return (
          <>
            <div className="live_position">
              <div className="label_img_wrapper">
                <Image src={CupIcon} alt="" width={22} height={22} />
              </div>
              <span>
                {tournamentLabelData.Rank}
                {nth(tournamentLabelData.Rank)}
                {` `} / <b>{tournamentLabelData.Point}</b> pts
              </span>
            </div>
          </>
        );
      }
      default:
        return null;
    }
  }, [tournamentLabelData]);

  const getTournamentLabelRankImg = () => {
    if (!prevRank) {
      return <div className="empty_position_icon" />;
    }

    let imageRef = null;

    if (prevRank < tournamentLabelData.Rank) {
      imageRef = NegativePolygonIcon;
    } else if (prevRank > tournamentLabelData.Rank) {
      imageRef = PositivePolygonIcon;
    }

    if (!imageRef) {
      return <div className="empty_position_icon" />;
    }

    return (
      <Image
        className="tournament_position_icon"
        src={imageRef}
        alt="position icon"
        width={15}
        height={15}
      />
    );
  };

  const getWebPImage = () => {
    if (imgError && noImage?.src) {
      return noImage.src;
    }

    if (gameData.ImageWebP && !gameData.ImageWebP.includes('http')) {
      return `${websiteConfig.cdnUrl}/${gameData.ImageWebP}`;
    }

    return gameData.ImageWebP;
  };

  const getImage = () => {
    if (imgError && noImage?.src) {
      return noImage.src;
    }

    if (gameData.Image && !gameData.Image.includes('http')) {
      return `${websiteConfig.cdnUrl}${gameData.Image}`;
    }

    return gameData.Image;
  };

  const getLabelVariant = () => {
    if (!tournamentLabelData.EndDate) {
      return 'null';
    }

    try {
      const endDate = new Date(tournamentLabelData.EndDate).getTime();

      const OneDay = new Date().getTime() + 24 * 60 * 60 * 1000;

      if (endDate < OneDay) {
        return 'error';
      }
      // eslint-disable-next-line no-empty
    } catch (e) {}

    return '';
  };

  if (!gameData) {
    return (
      <>
        <Div100vh id="game-page" className="page loading">
          <LogoLoader />
        </Div100vh>
        <style jsx>{styles}</style>
      </>
    );
  }

  return (
    <>
      <Div100vh id="game-page" className="page">
        {gameUrl ? (
          <>
            <iframe
              src={gameUrl}
              title={`${websiteConfig.name} ${gameData.Name}`}
              ref={iframeRef}
              allowFullScreen
            />
            {iframeLoading && (
              <div className="loader_wrapper iframe">
                <LogoLoader />
              </div>
            )}
          </>
        ) : (
          <>
            <div className="back_button">
              <LinkWrapper href="/">
                <IconButton>
                  <span className="icon-back" />
                </IconButton>
              </LinkWrapper>
            </div>
            <Image
              src={`${websiteConfig.cdnUrl}/${gameData.Image}`}
              className="game_background"
              layout="fill"
              alt={gameData.Name}
              quality={1}
            />
            <div className="game_page_inner">
              <h1 className="game_name">{gameData.Name}</h1>
              <div className="game_image_wrapper">
                <Image
                  className="game_image"
                  src={getImage()}
                  width={380}
                  height={380 * 0.673}
                  alt={gameData.Name}
                  onError={({ currentTarget }) => {
                    if (currentTarget) {
                      currentTarget.onerror = null;
                    }
                    setImgError(true);
                  }}
                />
                {/*<picture>*/}
                {/*  {gameData.ImageWebP && (*/}
                {/*    <source srcSet={getWebPImage()} type="image/webp" />*/}
                {/*  )}*/}
                {/*  {gameData.Image && (*/}
                {/*    <source srcSet={getImage()} type="image/jpeg" />*/}
                {/*  )}*/}
                {/*  <img*/}
                {/*    className="game_image"*/}
                {/*    src={getImage()}*/}
                {/*    alt={gameData.Name}*/}
                {/*    onError={({ currentTarget }) => {*/}
                {/*      if (currentTarget) {*/}
                {/*        currentTarget.onerror = null;*/}
                {/*      }*/}
                {/*      setImgError(true);*/}
                {/*    }}*/}
                {/*  />*/}
                {/*</picture>*/}
                <div
                  className="favorite_wrapper"
                  onClick={addRemoveFromFavorites}
                >
                  <span>{intl.messages.add_to_my_games}</span>
                  <Image
                    className="favorites_star"
                    src={isFavorite ? StarActiveGoldIcon : StarInactiveGoldIcon}
                    alt="favorites"
                    width={15}
                    height={15}
                  />
                </div>
              </div>
              <div className="buttons_wrapper">
                {gameData.InitUrl && (
                  <Button variant="contained" onClick={playGame}>
                    {intl.messages.play}
                  </Button>
                )}
                {gameData.InitDemoUrl && (
                  <Button variant="outlined" onClick={playDemoGame}>
                    {intl.messages.practice}
                  </Button>
                )}
              </div>
            </div>
          </>
        )}
        {!gameUrl && (gameData?.FooterTitle || gameData?.FooterDescription) && (
          <div className="game_info_content">
            {gameData.FooterTitle ? <h2>{gameData.FooterTitle}</h2> : <></>}
            {gameData.FooterDescription ? (
              <p>{gameData.FooterDescription}</p>
            ) : (
              <></>
            )}
          </div>
        )}
        {tournamentLabelData && user?.id && (
          <Label show variant={getLabelVariant()} onClick={onTournamentLabel}>
            {getTournamentLabelText()}
            {getTournamentLabelRankImg()}
          </Label>
        )}
      </Div100vh>
      <ProductSchemaOrg
        name={gameData.Name}
        image={
          (gameData.Image || '').includes('http')
            ? gameData.Image
            : websiteConfig.cdnUrl + gameData.Image
        }
        price={0}
        aggregateRating={{
          bestRating: gameData?.BestRating,
          ratingValue: gameData?.Rating,
          ratingCount: gameData?.RatingCount,
        }}
      />
      <style jsx>{styles}</style>
    </>
  );
}

GamePage.getInitialProps = async ({ res, req, query }) => {
  if (!isClient()) {
    cacheControl(res, 20);
  }

  let data = null;

  if (!isClient() && req?.url && !req?.url.includes('/_next/')) {
    try {
      data = await getGame_req(req.cookies, query.gameKey);
    } catch (e) {
      console.log('GamePage -> getInitialProps -> gamesData Error:', e);
    }
  }

  return { data };
};
